import Vue from 'vue'
import RosterHeadless from './RosterHeadless'

/**
 * 勤務表で［表示］→［保存］したのと同じ処理を、画面に出さずに1か月ぶん流す。
 *
 * ## 何のためか
 *
 * 打刻（ダッシュボードの退勤）は、開始・終了・休憩しか記録しない。その日の勤務時間・時間外・夜勤・
 * 法定休日・控除、定時後の休憩、月の合計は、勤務表の画面が計算して保存する。
 * 打刻した日と、勤務表で同じ時刻を入れて保存した日が同じ結果になるように、打刻のあと、
 * 勤務表の部品そのものを画面に付けずに立てて、月を読み込ませ、［保存］と同じ道を通す。
 * 式・検証・本文づくりは勤務表の部品にしか無い（ここにも RosterHeadless.js にも写しは無い）。
 *
 * 見える勤務表の［保存］と違うのは2つ：今日より後で、まだ行が無い日は、保存の本文へ入れない
 * （利用者が見ていない所で、先の日の行を作らない）。その「今日」は、端末の日付でなく、一覧の応答が
 * 持つサーバの今日（理由はどちらも RosterHeadless.js の頭）。
 * 送る行の中身と、月の合計は、見える勤務表と同じ。
 *
 * ## 使い方
 *
 * ```js
 * const result = await rosterHeadlessSync({ year: 2026, month: 9, store: this.$store })
 * if (result.ok) { ... 保存できた ... } else { ... result.stage と result.reason を見る ... }
 * ```
 *
 * - **例外を投げない。**何が起きても、結果を値で返す。打刻そのものは先に確定しているので、
 *   ここでの失敗を打刻の失敗として扱わないこと。
 * - **固まらない。**全体に時間切れを置いてある（既定 15 秒）。
 * - **同時には1つだけ流す。**続けて呼ばれたら、前の回が終わってから次を流す
 *   （同じ月を2つ同時に保存すると、後のほうが必ず断られる）。
 *
 * ## 引数
 *
 * - `year`・`month`：流す月（数でも文字でもよい）。日をまたぐ勤務は、出勤した日の月。
 * - `store`：画面の store（`this.$store`）。保存できたとき、サイドバーのバッジを取り直す合図をここへ出す。
 *   渡さないと流さない（保存できたのに失敗の扱いになるため）。
 * - `timeoutMs`：全体の時間切れ（ミリ秒）。既定 15000。
 * - `noticeWaitMs`：読み込めなかったとき、その理由の文が届くのを待つ長さ（ミリ秒）。既定 3000。
 * - `isCancelled`：呼んだ側が「もう打ち切ったか」を答える関数（省略できる）。真を返したら、保存へ進まない
 *   （始める前・読み込みのあと・保存の直前・保存の前の検証のあとで聞く。保存を送ったあとは止められない）。
 *   続けて呼ばれた分は前の回が終わるまで順番を待つので、呼んだ側が自分の時間切れで先に
 *   「保存できなかった」と決めたあとに、こちらが裏で保存まで進まないようにするための口。
 *   止めたときの stage は `timeout`。答えを聞けなかった（関数が投げた）ときも、保存へ進まない。
 *
 * ## 返す値
 *
 * ```js
 * {
 *   ok,        // 保存できたら true
 *   stage,     // どの段で終わったか（下の一覧）
 *   year, month,
 *   reason,    // 利用者に見せられる理由の文（勤務表の画面が出す文そのまま）。無ければ ''
 *   reasons,   // 理由の文の全部（検証の赤字は複数ある）。無ければ []
 *   status,    // 保存を断られたときの状態コード（応答が無ければ 0）。ほかは null
 *   posted,    // 保存の通信を送ったか（true で ok が false なら、保存できたかどうかは分からないことがある）
 *   notices,   // 勤務表の画面なら確認の小窓で出ていた文の全部
 *   detail     // 調べるための補足（利用者には見せない）
 * }
 * ```
 *
 * `stage` の一覧：
 *
 * | stage | 意味 | 保存の通信 | reason |
 * |---|---|---|---|
 * | `saved` | 保存できた | 送った | '' |
 * | `setup` | 引数が足りない（store・年月） | 送っていない | '' |
 * | `load` | 読み込めなかった、または読み込みの途中で知らせが出た | 送っていない | 出た知らせの文（届かなければ ''） |
 * | `unsupported` | 一覧の応答に打刻の印が無い、またはサーバの今日（server_today）が無い（それを返さない版のサーバ）。どちらかは detail に入る | 送っていない | '' |
 * | `locked` | 承認待ち・承認済みの月 | 送っていない | 勤務表の上に出る文 |
 * | `hidden` | 保存すると打刻した時刻が空で上書きされる月。サーバが時刻を隠して返した行がある（今日より後の日に打刻の時刻が入っている）か、一覧が時刻を返した行を未来日として扱っている（サーバの今日で決めるので、ふつうは起きない。守りとして残す）。保存の本文へ入れる日が1日も無い月（月の全部の日が、サーバの今日より後で、行も無い）も、ここ。どれかは detail に入る | 送っていない | '' |
 * | `unsettled` | 計算が出そろわなかった | 送っていない | '' |
 * | `totals` | 日ごとの値の合計と月の合計が合わなかった | 送っていない | '' |
 * | `validation` | 入力欄の検証が通らなかった | 送っていない | 各欄の下に出る文（1つ目） |
 * | `save` | 保存を断られた・通信できなかった | 送った | 勤務表の画面が知らせに出す文 |
 * | `timeout` | 時間切れ、または呼んだ側が打ち切った | posted を見る | '' |
 * | `error` | 思わぬ失敗 | posted を見る | '' |
 */

export const STAGE = Object.freeze({
  SAVED: 'saved',
  SETUP: 'setup',
  LOAD: 'load',
  UNSUPPORTED: 'unsupported',
  LOCKED: 'locked',
  HIDDEN: 'hidden',
  UNSETTLED: 'unsettled',
  TOTALS: 'totals',
  VALIDATION: 'validation',
  SAVE: 'save',
  TIMEOUT: 'timeout',
  ERROR: 'error'
})

const DEFAULT_TIMEOUT_MS = 15000
const DEFAULT_NOTICE_WAIT_MS = 3000
// 出そろったと見るまでに、値が続けて変わらなかった回数。
const STABLE_ROUNDS = 2
// 出そろうのを待つ上限（回）。描画が回り続けたときに、ここで止める。
const MAX_SETTLE_ROUNDS = 20

// 前の回が終わるのを待つための列。
let queue = Promise.resolve()

// 画面に出さずに動かす勤務表の部品（最初に使うときに1度だけ作る）。
let Headless = null
function headlessConstructor () {
  if (!Headless) Headless = Vue.extend(RosterHeadless)
  return Headless
}

export function rosterHeadlessSync (options = {}) {
  const run = queue.then(() => syncOnce(options))
  // 失敗しても列を止めない（syncOnce は投げないが、念のため）。
  queue = run.then(() => undefined, () => undefined)
  return run.catch(error => result(options, STAGE.ERROR, { detail: String(error) }))
}

function result (options, stage, extra = {}) {
  return {
    ok: stage === STAGE.SAVED,
    stage,
    year: options ? options.year : undefined,
    month: options ? options.month : undefined,
    reason: '',
    reasons: [],
    status: null,
    posted: false,
    notices: [],
    detail: '',
    ...extra
  }
}

async function syncOnce (options) {
  const year = Number(options && options.year)
  const month = Number(options && options.month)
  const store = options && options.store
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return result(options, STAGE.SETUP, { detail: '年月が読めない' })
  }
  if (!store || typeof store.commit !== 'function') {
    return result(options, STAGE.SETUP, { detail: 'store が渡されていない' })
  }
  const timeoutMs = positiveOr(options.timeoutMs, DEFAULT_TIMEOUT_MS)
  const noticeWaitMs = positiveOr(options.noticeWaitMs, DEFAULT_NOTICE_WAIT_MS)

  // 順番を待つあいだに、呼んだ側が打ち切っていた。部品を立てず、通信も出さない。
  if (cancelledByCaller(options)) {
    return result(options, STAGE.TIMEOUT, { detail: '始める前に、呼んだ側が打ち切った' })
  }

  // 途中の様子。時間切れ・思わぬ失敗のとき、どこまで進んだかを結果へ入れる。
  const progress = { vm: null, posted: false, cancelled: false, step: '立てる前' }
  // ここから先へ進んではいけないか（こちらの時間切れ、または呼んだ側の打ち切り）。
  progress.shouldStop = () => progress.cancelled || cancelledByCaller(options)
  let timer = null
  try {
    const timedOut = new Promise(resolve => {
      timer = setTimeout(() => resolve(null), timeoutMs)
    })
    const finished = flow({ options, year, month, store, noticeWaitMs, progress })
      .catch(error => result(options, STAGE.ERROR, { detail: `${progress.step}: ${String(error)}` }))
    const outcome = await Promise.race([finished, timedOut])
    const settled = outcome || result(options, STAGE.TIMEOUT, { detail: progress.step })
    return {
      ...settled,
      posted: progress.posted,
      notices: progress.vm ? progress.vm.headlessNotices.slice() : []
    }
  } catch (error) {
    return result(options, STAGE.ERROR, { posted: progress.posted, detail: `${progress.step}: ${String(error)}` })
  } finally {
    if (timer !== null) clearTimeout(timer)
    // ここから先へは進ませない（時間切れのあと、裏で保存まで進まないように）。
    progress.cancelled = true
    // 必ず片づける（立てた部品を残さない）。
    try {
      if (progress.vm) {
        progress.vm.headlessCancelled = true
        progress.vm.$destroy()
      }
    } catch (ignored) {
      // 片づけの失敗で結果を変えない。
    }
  }
}

async function flow ({ options, year, month, store, noticeWaitMs, progress }) {
  // 1. 立てる。画面（document）には付けない。store を渡す（保存できたときの合図の出し先）。
  progress.step = '立てる'
  const vm = new (headlessConstructor())({ store, propsData: { fromExternal: false } })
  progress.vm = vm
  // 保存の前の検証が通ったあとでも、打ち切られていたら保存へ進ませない（部品の側で聞く）。
  vm.headlessStopCheck = progress.shouldStop
  vm.$mount()

  // 2. 読み込む（勤務表で［表示］を押したのと同じ）。終わるまで待つ。
  progress.step = '読み込み'
  await new Promise(resolve => {
    const stop = vm.$watch('loading', value => {
      if (value) return
      stop()
      resolve()
    })
    vm.loadMonth(year, month)
  })

  // 読み込みのあいだに打ち切られた。出そろうのを待つ描き直し（重い）にも入らない。
  if (progress.shouldStop()) {
    return result(options, STAGE.TIMEOUT, { detail: '読み込みのあいだに打ち切られた' })
  }

  // 読み込めなかった（表が出ていない）／案件の基準を取り直せなかった／途中で知らせが出た。
  if (!vm.displayFlg || vm.headlessBasicsFailed || vm.headlessNotices.length) {
    // 理由の文は、失敗の報告を送り終えてから届く。少しだけ待つ。
    if (!vm.headlessNotices.length) await waitForNotice(vm, noticeWaitMs)
    return stopped(options, STAGE.LOAD, vm.headlessNotices)
  }

  // 一覧の応答に打刻の印が無い（印を返さない版のサーバ）。打刻した日の休憩を計算し直せないまま保存してしまう。
  if (!vm.headlessHasStampedFacts()) {
    return result(options, STAGE.UNSUPPORTED, { detail: '一覧の応答に打刻の印が無い' })
  }
  // 一覧の応答にサーバの今日が無い（それを返さない版のサーバ）。新しい行を作ってよい日の終点と、
  // 未来日として時刻を送らない日を、サーバの今日で決められない。端末の日付で代わりに決めない
  // （端末の日付が進んでいると、サーバから見て明日の、時刻の無い行を作ってしまう）。
  if (!vm.headlessServerToday()) {
    return result(options, STAGE.UNSUPPORTED, { detail: '一覧の応答にサーバの今日が無い' })
  }

  // 承認待ち・承認済みの月（勤務表でも［保存］を押せない）。
  if (vm.statusBtn) {
    return stopped(options, STAGE.LOCKED, [vm.lockReason].filter(Boolean))
  }

  // サーバが時刻を隠して返した行がある。保存すると、その行の打刻が空で上書きされて消える。
  // 端末の時計では判定しない（時計が進んだ端末で打刻した行も、ここで止める）。
  if (vm.headlessHasHiddenActual()) {
    return result(options, STAGE.HIDDEN, { detail: 'サーバが時刻を隠した行がある' })
  }
  // 一覧が時刻を返した行を、未来日として空にした。未来日はサーバの今日で決めるので、ふつうは起きない
  // （守りとして残す）。保存すると、同じく打刻が空で上書きされて消える。
  if (vm.headlessDropsReturnedTimes()) {
    return result(options, STAGE.HIDDEN, { detail: '一覧が返した時刻を、未来日として空にした行がある' })
  }

  // 3. 出そろうのを待つ。
  progress.step = '出そろうのを待つ'
  const settled = await settle(vm)
  if (!settled) {
    return result(options, STAGE.UNSETTLED)
  }
  // 待つあいだに知らせが届いていたら、流さない。
  if (vm.headlessNotices.length) {
    return stopped(options, STAGE.LOAD, vm.headlessNotices)
  }

  // 4. 突き合わせ。日ごとの値の合計と月の合計が合わなければ、送らない。
  progress.step = '突き合わせ'
  const mismatches = vm.headlessTotalMismatches()
  if (mismatches.length) {
    return result(options, STAGE.TOTALS, { detail: mismatches.join('・') })
  }

  // 5. 保存（勤務表の［保存］と同じ道。検証 → 本文づくり → 送る）。
  progress.step = '保存'
  if (progress.shouldStop()) {
    return result(options, STAGE.TIMEOUT, { detail: '保存の前に打ち切られた' })
  }
  // 送る日が1日も無い（月の全部の日が、サーバの今日より後で、行も無い）。空の本文を送らない。
  // 計算が済んでいない日は必ず行があるので、ここへ来る月には、記録するものも無い。
  if (!vm.headlessSendsAnyDay()) {
    return result(options, STAGE.HIDDEN, { detail: '保存の本文へ入れる日が1日も無い' })
  }
  const monthly = vm.headlessMonthly()
  const noticesBefore = vm.headlessNotices.length
  // 送り始めたことを、その場で覚える（月の合計の部品は、検証が通って送る直前に、送信中の印を立てる）。
  // 時間切れのとき「送ったあとかどうか」を結果へ入れるため。
  monthly.$watch('submit_flg', value => {
    if (value) progress.posted = true
  }, { sync: true })
  const outcome = await monthly.register()
  if (!outcome || outcome.outcome === 'busy') {
    return result(options, STAGE.ERROR, { detail: '保存を始められなかった' })
  }
  if (outcome.outcome === 'invalid') {
    // 検証は通ったが、そのあいだに打ち切られたので保存へ進まなかった（赤字が出たわけではない）。
    if (progress.shouldStop()) {
      return result(options, STAGE.TIMEOUT, { detail: '保存の前の検証のあいだに打ち切られた' })
    }
    await vm.$nextTick()
    return stopped(options, STAGE.VALIDATION, vm.headlessFieldErrors())
  }
  if (outcome.outcome === 'saved') {
    return result(options, STAGE.SAVED)
  }
  // 断られた・通信できなかった。勤務表の画面が知らせに出す文を、そのまま理由にする。
  const said = vm.headlessNotices.slice(noticesBefore)
  return {
    ...stopped(options, STAGE.SAVE, [outcome.message].concat(said).filter(Boolean).filter(unique)),
    status: outcome.status
  }
}

function stopped (options, stage, reasons) {
  const list = (reasons || []).map(String)
  return result(options, stage, { reason: list.length ? list[0] : '', reasons: list })
}

const unique = (value, index, list) => list.indexOf(value) === index

// 呼んだ側が、もう打ち切ったと言っているか。
// 答えを聞けなかった（関数が投げた）ときは、打ち切られたものとして扱う（保存へ進まない側へ倒す）。
function cancelledByCaller (options) {
  const ask = options && options.isCancelled
  if (typeof ask !== 'function') return false
  try {
    return ask() === true
  } catch (error) {
    return true
  }
}

const positiveOr = (value, fallback) => (
  Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback
)

// 描画2回ぶん待つ（子の部品が値を親へ返し、親が配列を差し替えるまでに、描画2回ぶんかかる）。
async function ticks (vm) {
  await vm.$nextTick()
  await vm.$nextTick()
}

/*
 * 出そろうのを待つ。
 *
 * 「待つ → 強制の描き直し → 待つ → 値の写しを取る」を、写しが続けて同じになるまで繰り返す。
 * 行ごとの子の部品（時間外・夜勤・法定休日・控除）は、計算した値をあとから親へ返す。
 * 親が描き直されないと、月の合計は古い値のまま残る。勤務表は自分でも描き直すが、
 * 画面に出さずに流すときは、ここでも描き直してから値を見る（二重の守り）。
 *
 * タイマーでは待たない。隠れたタブではタイマーが1秒きざみに遅らされ、退勤のあとすぐ別のタブへ
 * 移られると、ここで何十秒も止まるため。
 *
 * @returns 出そろったら true。上限まで繰り返しても値が動き続けたら false
 */
async function settle (vm) {
  let previous = null
  let same = 0
  for (let round = 0; round < MAX_SETTLE_ROUNDS; round++) {
    await ticks(vm)
    vm.$forceUpdate()
    await ticks(vm)
    const current = vm.headlessSnapshot()
    if (current === previous) {
      same++
      if (same >= STABLE_ROUNDS) return true
    } else {
      same = 0
      previous = current
    }
  }
  return false
}

// 知らせが1つ届くか、時間が過ぎるまで待つ。
function waitForNotice (vm, waitMs) {
  return new Promise(resolve => {
    const timer = setTimeout(done, waitMs)
    function done () {
      clearTimeout(timer)
      vm.$off('headless-notice', done)
      resolve()
    }
    vm.$on('headless-notice', done)
  })
}
